import { Box, Stack, ThemeProvider } from '@mui/material';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import PageHeader from 'components/v2/common/pageHeader';
import ViewToggle from 'components/v2/common/viewToggle';
import PageBackground from 'components/v2/PageBackground';
import DocumentTemplatesTabPanel from 'views/v2/servicesDocuments/components/documentTemplatesTabPanel';
import ServicesTabPanel from 'views/v2/servicesDocuments/components/servicesTabPanel';

import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import { useProviderOptions } from 'hooks/useProviderOptions';
import useTranslate from 'hooks/useTranslate';
import type { RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { ServicesDocumentsTab } from 'types/servicesDocuments';

export default function ServicesDocumentsPage() {
    const { translate } = useTranslate();
    const { role } = useAuth();
    const { service_provider_id: activeSpId } = useSelector(
        (state: RootState) => state.user.entity
    );
    const [activeTab, setActiveTab] = useState<ServicesDocumentsTab>('services');

    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const {
        options: providerOptions,
        optionsById,
        loading: providersLoading,
    } = useProviderOptions(isSuperSuperAdmin);

    const fixedProviderId = useMemo(() => {
        if (activeSpId == null || Number.isNaN(Number(activeSpId))) {
            return null;
        }
        return Number(activeSpId);
    }, [activeSpId]);

    const fixedProviderLabel =
        fixedProviderId != null ? optionsById.get(fixedProviderId) : undefined;

    const tabOptions = useMemo(
        () => [
            {
                id: 'services' as const,
                label: translate('servicesDocuments.tabs.services'),
            },
            {
                id: 'documentTemplates' as const,
                label: translate('servicesDocuments.tabs.documentTemplates'),
            },
        ],
        [translate]
    );

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground maxWidth="lg">
                <Stack spacing={2.5}>
                    <PageHeader title={translate('servicesDocuments.title')} />

                    <ViewToggle
                        ariaLabel={translate('servicesDocuments.tabs.aria')}
                        value={activeTab}
                        onChange={setActiveTab}
                        options={tabOptions}
                        sx={{
                            width: { xs: '100%', sm: 360 },
                            alignSelf: 'flex-start',
                            '& > button': {
                                flex: '1 1 0',
                                minWidth: 0,
                                padding: { xs: '8px', sm: '8px 16px' },
                                fontSize: { xs: 12, sm: 13 },
                            },
                        }}
                    />

                    <Box>
                        {activeTab === 'services' ? (
                            <ServicesTabPanel
                                userRole={role || ''}
                                providerOptions={providerOptions}
                                providersLoading={providersLoading}
                                fixedProviderId={fixedProviderId}
                                fixedProviderLabel={fixedProviderLabel}
                            />
                        ) : (
                            <DocumentTemplatesTabPanel
                                userRole={role || ''}
                                providerOptions={providerOptions}
                                providersLoading={providersLoading}
                                fixedProviderId={fixedProviderId}
                                fixedProviderLabel={fixedProviderLabel}
                            />
                        )}
                    </Box>
                </Stack>
            </PageBackground>
        </ThemeProvider>
    );
}
